import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import dayjs from 'dayjs';

import Button from '@mui/material/Button';
import { BreadcrumbHeader, StatusBadge, EmptyState } from '../../components/shared';
import PortalSelfFulfillModal from './PortalSelfFulfillModal';
import { partOrders, shipments, portalTicketMessages } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';import ContentCard from "../../components/shared/ContentCard";

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function InfoCard({ title, children }) {
  return (
    <Box sx={{ backgroundColor: '#ffffff', border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>{title}</Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>
    </Box>);

}

// ── Activities timeline ───────────────────────────────────────────────────────
function ActivityTimeline({ events }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {events.map((ev, i) =>
      <Box key={i} sx={{ display: 'flex', gap: '12px' }}>
          {/* Dot + connecting line */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: ev.color, mt: '3px', flexShrink: 0 }} />
            {i < events.length - 1 &&
          <Box sx={{ width: 2, flex: 1, backgroundColor: 'var(--grey-200)', my: '3px', minHeight: 16 }} />
          }
          </Box>
          {/* Text */}
          <Box sx={{ pb: i < events.length - 1 ? 2 : 0, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--grey-800)', lineHeight: 1.4 }}>
              {ev.label}
            </Typography>
            {ev.sublabel &&
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                {ev.sublabel}
              </Typography>
          }
            {ev.date && ev.date !== '—' &&
          <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px' }}>
                {ev.date}
              </Typography>
          }
          </Box>
        </Box>
      )}
    </Box>);

}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', flexShrink: 0, mr: 2 }}>{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' ?
        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', fontWeight: 500 }}>{value || '—'}</Typography> :

        value ?? <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
        }
      </Box>
    </Box>);

}

function PortalChatPanel({ messages, onSend }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ForumIcon sx={{ fontSize: 17, color: 'var(--grey-400)' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>Messages</Typography>
      </Box>

      {/* Message scroll area */}
      <Box
        ref={scrollRef}
        sx={{ height: 280,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' }
        }}>
        
        {messages.length === 0 &&
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              No messages yet. Send a message to get started.
            </Typography>
          </Box>
        }

        {messages.map((msg) => {
          const isMe = msg.sender === 'contact';
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              {!isMe &&
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', fontWeight: 500, mb: '3px', px: '2px' }}>
                  {msg.senderName}
                </Typography>
              }
              <Box sx={{ maxWidth: '80%',
                px: '12px',
                py: '8px',
                borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                backgroundColor: isMe ? '#009688' : 'var(--grey-100)',
                color: isMe ? '#fff' : 'var(--grey-900)'
              }}>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {msg.text}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>
                {fmtTime(msg.sentAt)}
              </Typography>
            </Box>);

        })}
      </Box>

      {/* Input row */}
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' } }}>
          
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField
          size="small"
          placeholder="Type a message…"
          multiline
          maxRows={3}
          fullWidth
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': {
              fontSize: '0.8125rem',
              borderRadius: 0,
              '& fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' }
            },
            '& .MuiInputBase-input': { py: '10px', px: '4px' }
          }} />
        
        <IconButton
          size="small"
          disabled={!inputText.trim()}
          onClick={handleSend}
          sx={{ flexShrink: 0,
            mr: '10px',
            backgroundColor: '#009688',
            color: '#fff',
            borderRadius: '8px',
            width: 32,
            height: 32,
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' }
          }}>
          
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </ContentCard>);

}

export default function PortalTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [fulfillOpen, setFulfillOpen] = useState(false);

  const base = partOrders.find((o) =>
  o.id === id && (
  o.customerName === currentUser.customerName || o.resellerName === currentUser.customerName)
  );
  const ticket = base;
  const [messages, setMessages] = useState(
    () => portalTicketMessages.filter((m) => m.partOrderId === (base?.id ?? ''))
  );

  const handleFulfillDone = () => {
    setFulfillOpen(false);
    navigate('/portal/tickets');
  };

  const handleSend = (text) => {
    setMessages((prev) => [...prev, {
      id: `portal-msg-${Date.now()}`,
      partOrderId: ticket.id,
      sender: 'contact',
      senderName: currentUser.name ?? 'You',
      text,
      sentAt: new Date().toISOString()
    }]);
  };

  if (!ticket) {
    return (
      <Box sx={{ p: 4 }}>
        <EmptyState title="Ticket not found" description="This ticket doesn't exist or you don't have access to it." />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography onClick={() => navigate('/portal/tickets')} sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            ← Back to My Tickets
          </Typography>
        </Box>
      </Box>);

  }

  const linkedShipment = ticket.shipmentId ?
  shipments.find((s) => s.id === ticket.shipmentId) :
  null;

  // Build activity timeline from ticket data
  const timelineEvents = (() => {
    const events = [];
    events.push({ label: 'Ticket opened', date: fmtDate(ticket.createdDate), color: '#009688' });

    if (ticket.status === 'Awaiting Quote') {
      events.push({ label: 'Quote requested from suppliers', date: fmtDate(ticket.createdDate), color: '#607d8b' });
    }

    if (['Fulfilled', 'Shipped', 'Completed'].includes(ticket.status) && ticket.fulfillmentMethod) {
      const method = ticket.fulfillmentMethod === 'OnSite' ? 'On-Site' : ticket.fulfillmentMethod;
      events.push({
        label: `Fulfilled — ${method}`,
        sublabel: ticket.locationName ?? null,
        date: '—',
        color: '#009688'
      });
    }

    if (linkedShipment) {
      events.push({
        label: 'Shipment dispatched',
        sublabel: linkedShipment.shipmentNumber,
        date: fmtDate(linkedShipment.createdDate),
        color: '#1565C0'
      });
      if (linkedShipment.status === 'Delivered') {
        events.push({ label: 'Delivered', date: '—', color: '#2E7D32' });
      }
    }

    if (ticket.status === 'Completed') {
      events.push({ label: 'Ticket completed', date: '—', color: '#2E7D32' });
    }

    return events;
  })();

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BreadcrumbHeader
        title={ticket.ticketNumber}
        subtitle={ticket.partDescription}
        crumbs={[
        { label: 'Portal' },
        { label: 'My Tickets', path: '/portal/tickets' },
        { label: ticket.ticketNumber }]
        } />

      <PortalSelfFulfillModal
        open={fulfillOpen}
        order={ticket}
        onClose={() => setFulfillOpen(false)}
        onFulfilled={handleFulfillDone} />

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* Left column — 60% */}
        <Box sx={{ flex: 3, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <InfoCard title="Order Details">
            <InfoRow label="Ticket #" value={ticket.ticketNumber} />
            <InfoRow label="Part #" value={ticket.partNumber} />
            <InfoRow label="Description" value={ticket.partDescription} />
            <InfoRow label="Customer" value={ticket.customerName} />
            <Divider sx={{ my: 1.5 }} />
            <InfoRow label="Created" value={fmtDate(ticket.createdDate)} />
            <InfoRow label="SLA" value={ticket.sla} />
            <InfoRow label="Fulfillment" value={ticket.fulfillmentMethod} />
            {ticket.locationName && <InfoRow label="Location" value={ticket.locationName} />}
          </InfoCard>

          {linkedShipment &&
          <InfoCard title="Linked Shipment">
              <InfoRow label="Shipment #" value={linkedShipment.shipmentNumber} />
              <InfoRow label="Carrier" value={linkedShipment.carrier} />
              <InfoRow label="Tracking #" value={linkedShipment.trackingNumber} />
              <InfoRow label="Status" value={<StatusBadge status={linkedShipment.status} />} />
              <InfoRow label="Created" value={fmtDate(linkedShipment.createdDate)} />
              <Box sx={{ mt: 1 }}>
                <Typography
                onClick={() => navigate('/portal/dispatch')}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                
                  View My Shipments →
                </Typography>
              </Box>
            </InfoCard>
          }

          {/* Messages */}
          <PortalChatPanel messages={messages} onSend={handleSend} />
        </Box>

        {/* Right column — 40% */}
        <Box sx={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <InfoCard title="Status">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Current Status</Typography>
              <StatusBadge status={ticket.status} />
            </Box>
            {ticket.status === 'Open' &&
            <Box sx={{ mt: 2 }}>
                <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => setFulfillOpen(true)}
                sx={{ textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#009688',
                  borderColor: '#009688',
                  '&:hover': { backgroundColor: '#e0f2f1', borderColor: '#00796B' },
                  boxShadow: 'none'
                }}>
                
                  Self Fulfill
                </Button>
              </Box>
            }
          </InfoCard>

          <InfoCard title="Activity">
            <ActivityTimeline events={timelineEvents} />
          </InfoCard>

          {ticket.fulfillmentPhoto &&
          <InfoCard title="Fulfillment Photo">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                onClick={() => setPhotoExpanded((v) => !v)}
                sx={{ borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--grey-200)',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                
                  <Box
                  component="img"
                  src={ticket.fulfillmentPhoto.dataUrl}
                  alt="Fulfillment photo"
                  sx={{ width: '100%',
                    display: 'block',
                    objectFit: 'cover',
                    maxHeight: photoExpanded ? 320 : 80,
                    transition: 'max-height 0.25s ease'
                  }} />
                
                  {!photoExpanded &&
                <Box sx={{ position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.18)'
                }}>
                      <Typography sx={{ fontSize: '0.6875rem', color: '#fff', fontWeight: 600, letterSpacing: '0.04em' }}>
                        Click to expand
                      </Typography>
                    </Box>
                }
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CameraAltIcon sx={{ fontSize: 15, color: 'var(--grey-400)' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                    Uploaded {ticket.fulfillmentPhoto.uploadedAt ?
                  dayjs(ticket.fulfillmentPhoto.uploadedAt).format('MMM D, YYYY [at] h:mm A') :
                  '—'}
                  </Typography>
                </Box>
                {ticket.serialNumber &&
              <InfoRow label="Serial #" value={ticket.serialNumber} />
              }
              </Box>
            </InfoCard>
          }
        </Box>
      </Box>
    </Box>);

}